import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { NavLink } from 'src/design/atoms/NavLink';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { Drawer } from 'src/design/molecules/Drawer';
import { TextInput } from 'src/design/atoms/TextInput';
import { useDebouncedValue } from '@mantine/hooks';
import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { JobDto } from 'src/api/FiretrolApiClient';
import { useJobs } from 'src/hooks/useJobs';

export interface IJobSelectorMobileBarProps {
  onChange?: (job: JobDto) => void;
}

const TRUNCATE_AT = 100;

/** @registry Compact job-switcher bar rendered in the mobile navigation header. */
export const JobSelectorMobileBar: React.FC<IJobSelectorMobileBarProps> = ({
  onChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedJob, jobs } = useJobs();
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 100);

  const isActivityRoute = location.pathname === '/activity';
  const activeJobId = isActivityRoute || !selectedJob ? null : selectedJob.id;
  const activeLabel =
    activeJobId && selectedJob
      ? `${selectedJob.jobNumber} — ${selectedJob.name}`
      : null;

  const handleClose = () => {
    setOpened(false);
    setSearch('');
  };

  const handleSelect = (job: JobDto) => {
    if (onChange) onChange(job);
    navigate(`/jobs/${job.id}/summary`);
    handleClose();
  };

  const query = debounced.trim().toLowerCase();
  const filtered = query
    ? jobs.filter(
        (j) =>
          String(j.jobNumber).toLowerCase().includes(query) ||
          j.name.toLowerCase().includes(query),
      )
    : jobs;
  const display = filtered.slice(0, TRUNCATE_AT);
  const truncated = filtered.length > TRUNCATE_AT;

  return (
    <>
      <UnstyledButton
        onClick={() => setOpened(true)}
        aria-label="Open Quick Navigation"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--mantine-spacing-xs)',
          padding: '0 var(--mantine-spacing-md)',
        }}
      >
        <Text
          size="sm"
          fw={activeLabel ? 500 : 400}
          c={activeLabel ? 'bright' : 'dimmed'}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {activeLabel ?? 'Quick Navigation'}
        </Text>
        <IconChevronDown
          size={18}
          style={{ color: 'var(--mantine-color-gray-6)', flexShrink: 0 }}
        />
      </UnstyledButton>

      <Drawer
        opened={opened}
        onClose={handleClose}
        position="right"
        size="md"
        title={<Title order={4}>Quick Navigation</Title>}
        padding={0}
        styles={{
          content: { display: 'flex', flexDirection: 'column' },
          body: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            padding: 0,
            overflow: 'hidden',
          },
          header: {
            padding: 'var(--mantine-spacing-md)',
            marginBottom: 0,
          },
        }}
      >
        <Stack gap="xs" p="xs" pt={0} style={{ flexShrink: 0 }}>
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder="Search by job number or name..."
            data-autofocus
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Group justify="space-between" gap="xs">
            <Text size="xs" c="dimmed">
              {filtered.length === 0
                ? 'No matches'
                : `${filtered.length} ${
                    filtered.length === 1 ? 'job' : 'jobs'
                  }`}
            </Text>
            {truncated && (
              <Text size="xs" c="dimmed">
                Showing first {TRUNCATE_AT} — refine your search
              </Text>
            )}
          </Group>
        </Stack>
        <Divider />
        <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
          {display.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="xl" px="md">
              No jobs match &ldquo;{search}&rdquo;.
            </Text>
          )}
          {display.map((job) => (
            <NavLink
              key={job.id}
              active={job.id === activeJobId}
              onClick={() => handleSelect(job)}
              label={
                <Text fw={500} size="sm" c="bright">
                  {job.jobNumber}
                </Text>
              }
              description={
                <Text size="xs" lineClamp={1}>
                  {job.name}
                </Text>
              }
            />
          ))}
        </ScrollArea>
      </Drawer>
    </>
  );
};
